import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatCard, Panel, Pill } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Plus, Upload, FileText, ShieldCheck, X, CheckCircle, XCircle, Eye,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_TONES = {
  not_submitted: "muted",
  submitted: "info",
  pending_review: "warning",
  verified: "success",
  rejected: "danger",
  expired: "danger",
  re_submission_required: "warning",
  archived: "muted",
};

const SUBJECT_LABELS = {
  student: "Student", father: "Father", mother: "Mother",
  guardian: "Guardian", pickup_person: "Pickup person",
  employee: "Employee", vendor: "Vendor",
};

export default function Documents() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkReview, setShowBulkReview] = useState(false);

  const isFamily = ["father", "mother", "guardian", "parent"].includes(user?.role);
  const canReview = ["super_admin", "franchise_hq_admin", "hq_admin",
    "branch_admin", "principal", "academic_coordinator"].includes(user?.role);

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [d, s, c] = await Promise.all([
        api.get("/documents", { params }),
        api.get("/documents/stats/summary"),
        api.get("/documents/categories"),
      ]);
      setDocs(d.data.items || []);
      setStats(s.data);
      setCategories(c.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not load");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const visible = docs.filter((d) =>
    !search ||
    d.document_type?.toLowerCase().includes(search.toLowerCase()) ||
    d.document_number?.toLowerCase().includes(search.toLowerCase()) ||
    d.subject_id?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div data-testid="documents-page">
      <PageHeader
        eyebrow="Documents"
        title={isFamily ? "Family documents" : canReview ? "Document review queue" : "My documents"}
        subtitle={
          isFamily
            ? "Upload, view and update your family's admission documents. Branch admins review and verify them."
            : canReview
            ? "Approve / reject submitted documents. Track expiry dates and re-submissions."
            : "Manage your personal onboarding documents."
        }
        action={
          <div className="flex gap-2 flex-wrap">
            {canReview && (
              <>
                <Link to="/app/hrms/kyc-review">
                  <Button variant="outline" data-testid="kyc-review-btn" className="border-mango text-mango-deep">
                    <ShieldCheck size={16} /> Employee KYC queue
                  </Button>
                </Link>
                <Link to="/app/documents-config">
                  <Button variant="outline" data-testid="doc-config-btn">
                    <ShieldCheck size={16} /> Configure required docs
                  </Button>
                </Link>
                <CsvExportButton />
                {selectedIds.size > 0 && (
                  <Button
                    onClick={() => setShowBulkReview(true)}
                    variant="outline"
                    className="border-mustard text-mustard-hover"
                    data-testid="bulk-review-btn"
                  >
                    Batch review ({selectedIds.size})
                  </Button>
                )}
              </>
            )}
            <Button
              className="bg-terracotta hover:bg-terracotta/90 text-white"
              onClick={() => setShowNew(true)}
              data-testid="new-doc-btn"
            >
              <Plus size={16} /> Add document
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total" value={stats?.total ?? 0} icon={FileText} accent="ink" />
        <StatCard label="Pending review" value={stats?.by_status?.pending_review ?? stats?.by_status?.submitted ?? 0} icon={ShieldCheck} accent="mustard" />
        <StatCard label="Verified" value={stats?.by_status?.verified ?? 0} icon={CheckCircle} accent="sage" />
        <StatCard label="Rejected" value={stats?.by_status?.rejected ?? 0} icon={XCircle} accent="terracotta" />
        <StatCard label="Expiring (30d)" value={stats?.expiring_in_30_days ?? 0} icon={ShieldCheck} accent="lavender" />
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <Input
          placeholder="Search by type / number / subject…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[220px]"
          data-testid="doc-search"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="p-2 rounded-lg border border-border bg-paper text-sm"
          data-testid="doc-status-filter"
        >
          <option value="">All statuses</option>
          {["submitted", "pending_review", "verified", "rejected",
            "re_submission_required", "expired", "not_submitted"]
            .map((s) => (<option key={s} value={s}>{s.replace(/_/g, " ")}</option>))}
        </select>
      </div>

      <Panel>
        {loading ? (
          <div className="py-12 text-center text-ink-soft text-sm">Loading…</div>
        ) : visible.length === 0 ? (
          <div className="py-12 text-center text-ink-soft text-sm">
            No documents yet. Click "Add document" to start.
          </div>
        ) : (
          <div className="space-y-2" data-testid="docs-list">
            {canReview && (
              <div className="flex items-center justify-between px-2 mb-2">
                <label className="text-xs text-ink-soft flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === visible.length && visible.length > 0}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds(new Set(visible.map((d) => d.document_id)));
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                    data-testid="select-all-checkbox"
                  />
                  Select all ({visible.length})
                </label>
                {selectedIds.size > 0 && (
                  <button onClick={() => setSelectedIds(new Set())} className="text-xs underline text-ink-soft" data-testid="clear-selection">
                    Clear selection
                  </button>
                )}
              </div>
            )}
            {visible.map((d) => {
              const selected = selectedIds.has(d.document_id);
              return (
                <div
                  key={d.document_id}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition ${
                    selected ? "bg-terracotta-soft border-terracotta" : "bg-paper hover:bg-paper-alt border-border"
                  }`}
                  data-testid={`doc-${d.document_id}`}
                >
                  {canReview && (
                    <input
                      type="checkbox"
                      className="mr-3"
                      checked={selected}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const next = new Set(selectedIds);
                        if (e.target.checked) next.add(d.document_id);
                        else next.delete(d.document_id);
                        setSelectedIds(next);
                      }}
                      data-testid={`select-${d.document_id}`}
                    />
                  )}
                  <button
                    onClick={() => setSelected(d)}
                    className="flex-1 flex items-center justify-between text-left"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {(d.document_name || d.document_type || "—").replace(/_/g, " ")}
                        <span className="text-ink-soft text-xs ml-2">
                          ({SUBJECT_LABELS[d.subject_kind] || d.subject_kind})
                        </span>
                      </div>
                      <div className="text-xs text-ink-soft truncate">
                        #{d.document_number || "—"} · by {d.submitted_by_name || "—"}
                        {d.expiry_date ? ` · expires ${d.expiry_date.slice(0,10)}` : ""}
                      </div>
                    </div>
                    <Pill tone={STATUS_TONES[d.status] || "muted"}>
                      {(d.status || "").replace(/_/g, " ")}
                    </Pill>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {showNew && (
        <NewDocumentModal
          categories={categories}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); load(); }}
        />
      )}
      {showBulkReview && (
        <BulkReviewModal
          ids={Array.from(selectedIds)}
          docs={docs.filter((d) => selectedIds.has(d.document_id))}
          onClose={() => setShowBulkReview(false)}
          onDone={() => { setShowBulkReview(false); setSelectedIds(new Set()); load(); }}
        />
      )}
      {selected && (
        <DocumentDetailModal
          doc={selected}
          canReview={canReview}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}


// ─── CSV Export Button ────────────────────────────────────────────


function CsvExportButton() {
  const [open, setOpen] = useState(false);
  async function download(kind) {
    try {
      const res = await api.get(`/documents/reports/${kind}.csv`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `documents_${kind}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not export");
    } finally {
      setOpen(false);
    }
  }
  return (
    <div className="relative inline-block">
      <Button
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        data-testid="csv-export-toggle"
      >
        <FileText size={16} /> Export CSV
      </Button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-border rounded-xl p-2 shadow-floating min-w-[220px]">
          {[
            ["admission_pending", "Admission pending"],
            ["employee_onboarding", "Employee onboarding"],
            ["vendor", "Vendor"],
            ["expiring", "Expiring (30 days)"],
            ["rejected", "Rejected"],
            ["verified", "Verified"],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => download(k)}
              className="w-full text-left p-2 text-sm rounded-lg hover:bg-paper-alt"
              data-testid={`csv-${k}`}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Bulk Review Modal ────────────────────────────────────────────


function BulkReviewModal({ ids, docs, onClose, onDone }) {
  const [decision, setDecision] = useState("verified");
  const [sharedRemarks, setSharedRemarks] = useState("");
  const [individualRemarks, setIndividualRemarks] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      const items = ids.map((id) => ({
        document_id: id,
        decision,
        remarks: individualRemarks[id] || undefined,
      }));
      const r = await api.post("/documents/bulk-review", {
        items,
        shared_remarks: sharedRemarks || undefined,
      });
      toast.success(`Reviewed ${r.data.success.length} document${r.data.success.length !== 1 ? "s" : ""}` +
        (r.data.failures.length ? ` — ${r.data.failures.length} failed` : ""));
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Bulk review failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-floating max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-review-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-heading font-bold">
            Batch review · {ids.length} document{ids.length !== 1 ? "s" : ""}
          </div>
          <button onClick={onClose} className="text-ink-soft"><X size={18} /></button>
        </div>

        {!confirm ? (
          <>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Apply decision</label>
            <select
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              className="w-full mt-1 mb-3 p-2 rounded-lg border border-border bg-paper text-sm"
              data-testid="bulk-decision"
            >
              <option value="verified">Verify ✅ all selected</option>
              <option value="rejected">Reject ❌ all selected</option>
              <option value="re_submission_required">Re-submission required</option>
            </select>

            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Shared remark (applied to all)</label>
            <Textarea
              value={sharedRemarks}
              onChange={(e) => setSharedRemarks(e.target.value)}
              rows={2}
              placeholder="e.g. Originals presented in person, batch verified"
              data-testid="bulk-shared-remarks"
            />

            <div className="mt-4 mb-1 text-xs font-semibold uppercase tracking-wide text-ink-soft">Optional per-document remarks ({docs.length}):</div>
            <div className="max-h-64 overflow-y-auto border border-border rounded-lg p-2 space-y-2 bg-paper-alt">
              {docs.map((d) => (
                <div key={d.document_id} className="flex gap-2 items-start" data-testid={`bulk-row-${d.document_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {(d.document_name || d.document_type || "").replace(/_/g, " ")} · {SUBJECT_LABELS[d.subject_kind] || d.subject_kind}
                    </div>
                    <div className="text-xs text-ink-soft truncate">
                      by {d.submitted_by_name} · #{d.document_number || "—"}
                    </div>
                  </div>
                  <input
                    placeholder="Note (optional)"
                    value={individualRemarks[d.document_id] || ""}
                    onChange={(e) => setIndividualRemarks({ ...individualRemarks, [d.document_id]: e.target.value })}
                    className="flex-1 p-1.5 text-xs rounded-lg border border-border bg-white"
                    data-testid={`bulk-remark-${d.document_id}`}
                  />
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button
                onClick={() => setConfirm(true)}
                className="bg-terracotta hover:bg-terracotta/90 text-white"
                data-testid="bulk-next-btn"
              >
                Continue → Confirm
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-mustard-soft rounded-xl p-4 mb-3 text-sm">
              You are about to apply <strong>{decision.replace(/_/g, " ")}</strong> to{" "}
              <strong>{ids.length}</strong> document{ids.length !== 1 ? "s" : ""}.
              {sharedRemarks && (
                <div className="mt-2 italic">"{sharedRemarks}"</div>
              )}
              <div className="mt-2 text-ink-soft">This action is audited and cannot be undone.</div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirm(false)} disabled={submitting}>← Back</Button>
              <Button
                onClick={submit}
                disabled={submitting}
                className="bg-terracotta hover:bg-terracotta/90 text-white"
                data-testid="bulk-confirm-submit"
              >
                {submitting ? "Applying…" : `Apply to ${ids.length}`}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function NewDocumentModal({ categories, onClose, onCreated }) {
  const { user } = useAuth();
  const [category, setCategory] = useState("student");
  const [docType, setDocType] = useState("birth_certificate");
  const [subjectKind, setSubjectKind] = useState("student");
  const [subjectId, setSubjectId] = useState("");
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({
    document_name: "",
    document_number: "",
    name_as_per_document: "",
    issuing_authority: "",
    issue_date: "",
    expiry_date: "",
    remarks: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/students");
        const items = r.data || [];
        setStudents(items);
        if (items[0] && subjectKind === "student") setSubjectId(items[0].student_id);
      } catch (e) { /* ignore */ }
    })();
    /* eslint-disable-next-line */
  }, []);

  function categoryChanged(c) {
    setCategory(c);
    const opts = categories?.categories?.[c] || [];
    setDocType(opts[0] || "");
    const skMap = { student: "student", parent: "father", employee: "employee", vendor: "vendor" };
    setSubjectKind(skMap[c] || "student");
    if (c === "student") setSubjectId(students[0]?.student_id || "");
    else if (c === "employee" || c === "vendor") setSubjectId(user.user_id);
    else if (c === "parent") setSubjectId(user.user_id);
  }

  async function submit() {
    if (!subjectId) {
      toast.error("Pick a subject (student/employee/vendor)");
      return;
    }
    setSubmitting(true);
    try {
      let file_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        file_id = up.data.file_id;
      }
      await api.post("/documents", {
        subject_kind: subjectKind,
        subject_id: subjectId,
        category,
        document_type: docType,
        document_name: docType === "other" ? form.document_name : null,
        document_number: form.document_number || null,
        name_as_per_document: form.name_as_per_document || null,
        issuing_authority: form.issuing_authority || null,
        issue_date: form.issue_date || null,
        expiry_date: form.expiry_date || null,
        file_id,
        remarks: form.remarks || null,
      });
      toast.success("Document submitted");
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save");
    } finally {
      setSubmitting(false);
    }
  }

  const typeOptions = categories?.categories?.[category] || [];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-6 max-w-xl w-full shadow-floating max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-doc-modal"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-heading font-bold">📄 Add a document</div>
          <button onClick={onClose} className="text-ink-soft hover:text-ink"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Category</label>
            <select value={category} onChange={(e) => categoryChanged(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-doc-category">
              <option value="student">Student admission doc</option>
              <option value="parent">Parent / Guardian doc</option>
              <option value="employee">Employee onboarding doc</option>
              <option value="vendor">Vendor doc</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Document type</label>
            <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-doc-type">
              {typeOptions.map((t) => (
                <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
              ))}
            </select>
          </div>
        </div>

        {category === "student" && students.length > 0 && (
          <div className="mb-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">For child</label>
            <select value={subjectId} onChange={(e) => setSubjectId(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-doc-student">
              {students.map((s) => (<option key={s.student_id} value={s.student_id}>{s.name}</option>))}
            </select>
          </div>
        )}

        {category === "parent" && (
          <div className="mb-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Subject (whose document is this?)</label>
            <select value={subjectKind} onChange={(e) => setSubjectKind(e.target.value)} className="w-full mt-1 p-2 rounded-lg border border-border bg-paper text-sm" data-testid="new-doc-subject-kind">
              <option value="father">Father</option>
              <option value="mother">Mother</option>
              <option value="guardian">Guardian</option>
            </select>
          </div>
        )}

        {docType === "other" && (
          <div className="mb-3">
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Document name</label>
            <Input value={form.document_name} onChange={(e) => setForm({ ...form, document_name: e.target.value })} placeholder="e.g. Caste certificate" data-testid="new-doc-name" />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Document number</label>
            <Input value={form.document_number} onChange={(e) => setForm({ ...form, document_number: e.target.value })} data-testid="new-doc-number" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Name as per document</label>
            <Input value={form.name_as_per_document} onChange={(e) => setForm({ ...form, name_as_per_document: e.target.value })} data-testid="new-doc-name-asper" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Issuing authority</label>
            <Input value={form.issuing_authority} onChange={(e) => setForm({ ...form, issuing_authority: e.target.value })} data-testid="new-doc-issuer" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Issue date</label>
            <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} data-testid="new-doc-issue-date" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Expiry date (optional)</label>
            <Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="new-doc-expiry-date" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Attach file (PDF / image)</label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full mt-1 text-xs" accept="image/*,application/pdf" data-testid="new-doc-file" />
          </div>
        </div>

        <div className="mb-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-ink-soft">Remarks</label>
          <Textarea value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} rows={2} placeholder="Optional notes" data-testid="new-doc-remarks" />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-terracotta hover:bg-terracotta/90 text-white"
            data-testid="new-doc-submit"
          >
            {submitting ? "Saving…" : <><Upload size={14} /> Submit</>}
          </Button>
        </div>
      </div>
    </div>
  );
}


function DocumentDetailModal({ doc, canReview, onClose, onChanged }) {
  const [decision, setDecision] = useState("verified");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  async function review() {
    setSaving(true);
    try {
      await api.post(`/documents/${doc.document_id}/review`, { decision, remarks });
      toast.success(`Document ${decision.replace(/_/g, " ")}`);
      onChanged();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not review");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl p-6 max-w-2xl w-full shadow-floating max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="doc-detail-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-lg font-heading font-bold capitalize">
              {(doc.document_name || doc.document_type || "Document").replace(/_/g, " ")}
            </div>
            <div className="text-xs text-ink-soft">
              {SUBJECT_LABELS[doc.subject_kind]} · by {doc.submitted_by_name || "—"} · {doc.submitted_at?.slice(0, 10)}
            </div>
          </div>
          <Pill tone={STATUS_TONES[doc.status] || "muted"}>{(doc.status || "").replace(/_/g, " ")}</Pill>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
          <Field label="Document number">{doc.document_number || "—"}</Field>
          <Field label="Name as per doc">{doc.name_as_per_document || "—"}</Field>
          <Field label="Issuing authority">{doc.issuing_authority || "—"}</Field>
          <Field label="Issue date">{doc.issue_date || "—"}</Field>
          <Field label="Expiry">{doc.expiry_date || "—"}</Field>
          <Field label="File">
            {doc.file_id ? (
              <a href={`/api/files/${doc.file_id}`} target="_blank" rel="noreferrer" className="text-terracotta underline inline-flex items-center gap-1" data-testid="doc-view-file">
                <Eye size={14} /> View file
              </a>
            ) : (
              "Not uploaded"
            )}
          </Field>
          {doc.remarks && <div className="col-span-2"><Field label="Remarks">{doc.remarks}</Field></div>}
        </div>

        {doc.history && doc.history.length > 0 && (
          <div className="mb-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft mb-2">History</div>
            <div className="space-y-1 text-xs">
              {doc.history.slice().reverse().map((h, i) => (
                <div key={i} className="flex gap-2">
                  <div className="text-ink-soft min-w-[110px]">{(h.ts || "").slice(0, 16).replace("T", " ")}</div>
                  <div>
                    <span className="font-semibold">{h.by_name || h.by}</span>{" "}
                    <span className="text-ink-soft">{h.event}</span>
                    {h.remarks && <span className="italic"> — {h.remarks}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {canReview && doc.status !== "verified" && doc.status !== "archived" && (
          <div className="border-t border-border pt-4 space-y-3" data-testid="doc-review-form">
            <div className="text-sm font-semibold">Review this document</div>
            <select value={decision} onChange={(e) => setDecision(e.target.value)} className="w-full p-2 rounded-lg border border-border bg-paper text-sm" data-testid="doc-decision">
              <option value="verified">Verify ✅</option>
              <option value="rejected">Reject ❌</option>
              <option value="re_submission_required">Ask for re-submission</option>
            </select>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Remarks for the family / employee / vendor" data-testid="doc-review-remarks" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>Close</Button>
              <Button onClick={review} disabled={saving} className="bg-terracotta hover:bg-terracotta/90 text-white" data-testid="doc-review-submit">
                {saving ? "Saving…" : "Apply decision"}
              </Button>
            </div>
          </div>
        )}
        {(!canReview || doc.status === "verified" || doc.status === "archived") && (
          <div className="flex justify-end pt-3 border-t border-border">
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-ink-soft">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
